import {
  type JobScopeChangeItem as JobScopeChangeItemProto,
  JobScopeChangePriceBreakdownForAdminResponse as JobScopeChangePriceBreakdownForAdminResponseProto,
  JobScopeChangeStatus as JobScopeChangeStatusProto,
  type JobScopeChangeStatusAggregations as JobScopeChangeStatusAggregationsProto,
  JobScopeItemType as JobScopeItemTypeProto,
  JobScopeResponderType as JobScopeResponderTypeProto,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import { Id } from '@/infra/id'
import { safeDecimalToNumber } from '@/utils/grpc-util'
import { match } from '@/utils/pattern-match-util'
import {
  type JobScopeChangeItemBreakdownKind,
  type JobScopeChangeItemPriceType,
  type ResponderType,
  type ResponseType,
  type ScopeChangeRequestItemType,
  type ScopeChangeRequestStatus,
  type ScopeChangePriceBreakDownItemForAdminProps,
  type ScopeChangeRequestItemProps,
  type ScopeChangeRequestPriceBreakDownProps,
  ScopeChangePriceBreakDownItemForAdmin,
} from './ScopeChangeRequest'
import { type ScopeChangeRequestByStatusCount } from './ScopeChangeRequestByStatusCount'

const ItemBreakdownProto =
  JobScopeChangePriceBreakdownForAdminResponseProto.JobScopeChangeItemBreakdownForAdmin

export function mapScopeChangeRequestStatusProtoToStatus(
  status: JobScopeChangeStatusProto,
): ScopeChangeRequestStatus {
  return match(status, {
    [JobScopeChangeStatusProto.PENDING_FINALIZATION]: 'PendingFinalization',
    [JobScopeChangeStatusProto.ACCEPTED]: 'Accepted',
    [JobScopeChangeStatusProto.PENDING_ADMIN_APPROVAL]: 'PendingAdminApproval',
    [JobScopeChangeStatusProto.PENDING_CLIENT_APPROVAL]:
      'PendingClientApproval',
    [JobScopeChangeStatusProto.REJECTED]: 'Rejected',
    [JobScopeChangeStatusProto.APPLY_FAILED]: 'ApplyFailed',
    [JobScopeChangeStatusProto.UNSPECIFIED]: 'Unspecified',
  })
}

export function mapResponderTypeProtoToResponderType(
  type: JobScopeResponderTypeProto,
): ResponderType {
  return match(type, {
    [JobScopeResponderTypeProto.CLIENT]: 'Client',
    [JobScopeResponderTypeProto.ADMIN]: 'Admin',
    [JobScopeResponderTypeProto.SYSTEM]: 'System',
  })
}

export function mapResponseTypeProtoToResponseType(type: string): ResponseType {
  return match<string, ResponseType>(type, {
    Approved: 'Approved',
    Rejected: 'Rejected',
  })
}

function mapItemTypeProtoToItemType(
  type: JobScopeItemTypeProto,
): ScopeChangeRequestItemType {
  return match(type, {
    [JobScopeItemTypeProto.INVENTORY]: 'Inventory',
    [JobScopeItemTypeProto.CUSTOM]: 'Custom',
  })
}

export function mapScopeChangeRequestItemProtoToProps(
  proto: JobScopeChangeItemProto,
): ScopeChangeRequestItemProps {
  return {
    id: Id.assert(proto.getId()),
    name: proto.getName(),
    quantity: proto.getQuantity(),
    price: safeDecimalToNumber(proto.getPrice()),
    proPrice: safeDecimalToNumber(proto.getProPrice()),
    type: mapItemTypeProtoToItemType(proto.getType()),
    inventoryItemVersionId: Id(proto.getInventoryItemVersionId()?.getValue()),
  }
}

export function mapScopeChangeRequestPriceBreakDownProtoToProps(
  proto: JobScopeChangePriceBreakdownForAdminResponseProto,
): ScopeChangeRequestPriceBreakDownProps {
  return {
    id: Id.assert(proto.getId()),
    status: mapScopeChangeRequestStatusProtoToStatus(proto.getStatus()),
    previousTotalPrice: safeDecimalToNumber(proto.getPreviousTotalPrice()),
    newTotalPrice: safeDecimalToNumber(proto.getNewTotalPrice()),
    previousTotalProPrice: safeDecimalToNumber(
      proto.getPreviousTotalProPrice(),
    ),
    newTotalProPrice: safeDecimalToNumber(proto.getNewTotalProPrice()),
    items: proto
      .getItemsList()
      .map(
        (item) =>
          new ScopeChangePriceBreakDownItemForAdmin(
            mapScopeChangePriceBreakDownItemProtoToProps(item),
          ),
      ),
  }
}

export function mapScopeChangePriceBreakDownItemProtoToProps(
  proto: JobScopeChangePriceBreakdownForAdminResponseProto.JobScopeChangeItemBreakdownForAdmin,
): ScopeChangePriceBreakDownItemForAdminProps {
  const bundleAttribution = proto.getBundleAttribution()

  return {
    description: proto.getDescription(),
    inventoryItemVersionId: Id(proto.getInventoryItemVersionId()?.getValue()),
    name: proto.getName(),
    newPrice: safeDecimalToNumber(proto.getNewPrice()),
    newProPrice: safeDecimalToNumber(proto.getNewProPrice()),
    previousPrice: safeDecimalToNumber(proto.getPreviousPrice()),
    previousProPrice: safeDecimalToNumber(proto.getPreviousProPrice()),
    newQuantity: proto.getNewQuantity(),
    previousQuantity: proto.getPreviousQuantity(),
    priceType: mapPriceTypeProtoToPriceType(proto.getPriceType()),
    bundleId: Id(bundleAttribution?.getBundleId()?.getValue()),
    bundleName: bundleAttribution?.getBundleName()?.getValue() ?? null,
    kind: mapBreakdownKindProtoToBreakdownKind(proto.getKind()),
  }
}

export function mapStatusAggregationProtoToScopeChangeRequestByStatusCount(
  proto: JobScopeChangeStatusAggregationsProto,
): ScopeChangeRequestByStatusCount {
  return {
    status: mapScopeChangeRequestStatusProtoToStatus(proto.getStatus()),
    total: proto.getTotal(),
  }
}

function mapPriceTypeProtoToPriceType(
  priceType: JobScopeChangePriceBreakdownForAdminResponseProto.JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemPriceType,
): JobScopeChangeItemPriceType {
  const PriceTypeProto = ItemBreakdownProto.JobScopeChangeItemPriceType
  return match(priceType, {
    [PriceTypeProto.FEE]: 'Fee',
    [PriceTypeProto.FLAT]: 'Flat',
    [PriceTypeProto.MARKUP]: 'Markup',
    [PriceTypeProto.MARKDOWN]: 'Markdown',
    [PriceTypeProto.CUSTOM]: 'Custom',
  })
}

function mapBreakdownKindProtoToBreakdownKind(
  kind: JobScopeChangePriceBreakdownForAdminResponseProto.JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemBreakdownKind,
): JobScopeChangeItemBreakdownKind {
  const KindProto = ItemBreakdownProto.JobScopeChangeItemBreakdownKind
  return match(kind, {
    [KindProto.JOB_SCOPE_CHANGE_ITEM_BREAKDOWN_KIND_UNSPECIFIED]: 'Unspecified',
    [KindProto.SERVICE_ITEM]: 'ServiceItem',
    [KindProto.BUNDLE]: 'Bundle',
    [KindProto.BUNDLE_INCLUDED_ITEM]: 'BundleIncludedItem',
  })
}
